import React, { useEffect, useRef, useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { AppFormDrawer, AppNote } from "@components"
import { dsColorNew } from "@palette"
import {
	isDsRepricingDrawerOpen, dsRepricingDrawerData, dsRepricingDrawerErrors, clearDsRepricingSignals,
} from "Pages/DsRepricing/Page/@signals"
import DsRepricingDrawerFooter from "Pages/DsRepricing/Page/@components/Footer"
import DsRepricingName from "Pages/DsRepricing/Page/@components/Name"
import DsRepricingPriority from "Pages/DsRepricing/Page/@components/Priority"
import DsRepricingPeriod from "Pages/DsRepricing/Page/@components/Period"
import DsRepricingDurations from "Pages/DsRepricing/Page/@components/DurationRange"
import DsRepricingStrategy from "Pages/DsRepricing/Page/@components/Strategy"
import DsRepricingUndercut from "Pages/DsRepricing/Page/@components/Undercut"
import DsRepricingCompetitorRule from "Pages/DsRepricing/Page/@components/CompetitorRule"
import DsRepricingTethers  from "Pages/DsRepricing/Page/@components/Tethers"
import DsRepricingKpiConditions from "Pages/DsRepricing/Page/@components/KpiConditions"
import DsRepricingExcludeOffices from "Pages/DsRepricing/Page/@components/ExcludeOffices"
import DsRepricingExcludeWebs from "Pages/DsRepricing/Page/@components/ExcludeWebs"
import DsRepricingExcludeWebSuppliers from "Pages/DsRepricing/Page/@components/ExcludeWebSuppliers"
import DsRepricingExcludeProviders from "Pages/DsRepricing/Page/@components/ExcludeProviders"
import DsRepricingFilterProviders from "Pages/DsRepricing/Page/@components/FilterProviders"
import DsRepricingFilterCategories from "Pages/DsRepricing/Page/@components/FilterCategories"
import DsRepricingFilterAcriss from "Pages/DsRepricing/Page/@components/FilterAcriss"
import DsRepricingCompareBy from "Pages/DsRepricing/Page/@components/CompareBy"
import DsRepricingFaceScope from "Pages/DsRepricing/Page/@components/FaceScope"
import DsRepricingCompareEveryKind from "Pages/DsRepricing/Page/@components/CompareEveryKind"
import DsRepricingRateCardsFit, { useRateCardsFit } from "Pages/DsRepricing/Page/@components/RateCardsFit"
import DsRepricingStepper from "Pages/DsRepricing/Page/Drawer/@components/Stepper"
import SectionCard from "Pages/DsRepricing/@components/SectionCard"
import { DS_REPRICING_STEPS, stepIsValid } from "Pages/DsRepricing/Page/Drawer/steps"

// A compact banner surfaced at the top of the drawer. Lists what used to
// live inside a strategy and now belongs to Setup so the operator does
// not look for the moved knobs inside the wizard.
function MovedToSetupBanner() {
	return (
		<AppNote>
			<Box component="span" sx={{ fontWeight: 600 }}>
				Setup vs strategy:
			</Box>{' '}
			<Box component="span">
				Category consistency, Price limits and Rate cards are global
				rules defined in <em>Setup</em>; they apply on top of every
				strategy that lands in their scope. Tethering rules also live
				in <em>Setup</em>, but each strategy picks which ones it uses
				(step 3). A strategy just decides <em>when</em>, <em>on
				what</em>, and <em>how the price is calculated</em>.
			</Box>
		</AppNote>
	)
}

// A label between two groups of cards, in the stepper's kicker type: the
// scope step holds what the rule prices and what it is compared against,
// and the two have to read as two blocks, not six cards in a column
// (CU-869fe6mf4, UX decision 3). A line fills the rest of the row.
function GroupLabel({ children }) {
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
			<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6,
			                  textTransform: 'uppercase', color: dsColorNew.textSubtle,
			                  whiteSpace: 'nowrap' }}>
				{children}
			</Typography>
			<Box sx={{ flex: 1, height: '1px', bgcolor: dsColorNew.grayBorder }}/>
		</Box>
	)
}

// Every panel stays mounted and is hidden with `display`, not unmounted: the field components own
// local state and publish their validation into `dsRepricingDrawerErrors`, so unmounting the steps
// the user has not opened would silently drop both.
function Panel({ step, current, title, description, children }) {
	return (
		<Box sx={{ display: step === current ? 'block' : 'none' }}>
			<Box sx={{ mb: 2.25 }}>
				<Typography sx={{ fontSize: 15, fontWeight: 600, color: dsColorNew.textTitle, mb: 0.625 }}>{title}</Typography>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, maxWidth: 560, lineHeight: 1.5 }}>
					{description}
				</Typography>
			</Box>
			{children}
		</Box>
	)
}

export default function DsRepricingDrawer({ onSubmit, isLoading }) {
	const isEdit = !!dsRepricingDrawerData.peek().id
	const isOpen = isDsRepricingDrawerOpen.value
	const compareByCategory = dsRepricingDrawerData.value?.compareByCategory ?? false
	// Ranking is the only strategy with a target position, so it is the
	// only one the incomplete-market adjustment can reach.
	const isRanking = dsRepricingDrawerData.value?.strategy?.type === 'ranking'
	const [current, setCurrent] = useState(1)
	// A step change has to put the reader back at the top. The steps are
	// long enough to scroll, and "Next" is at the bottom, so advancing
	// left the operator looking at the footer of a step they had not
	// read a word of (Curro, 2026-09-28). Anchoring to the stepper —
	// rather than scrolling some ancestor by hand — works whichever
	// element the drawer actually scrolls.
	const topRef = useRef(null)
	const previousStep = useRef(1)
	useEffect(() => {
		if (previousStep.current === current) return
		previousStep.current = current
		topRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
	}, [current])

	useEffect(() => {
		if (isOpen) setCurrent(1)
	}, [isOpen])

	const errors = dsRepricingDrawerErrors.value
	// Whether any rate card fits the scope in the form (CU-869fh646w, UX
	// D5): the footer says it in red on the steps that can save, because
	// an operator who narrows step 2 and saves from step 3 never sees the
	// block again. Save stays enabled.
	const { noFit } = useRateCardsFit()
	const invalidSteps = DS_REPRICING_STEPS
		.filter(s => !stepIsValid(s.step, errors))
		.map(s => s.step)

	return <AppFormDrawer
		icon={isEdit ? 'pen' : 'file-plus'}
		title={isEdit ? dsRepricingDrawerData.peek().name : "Create DS Repricing Configuration"}
		width="1120px"
		isOpen={isDsRepricingDrawerOpen.value}
		// No `variant`: DrawerSeeker only forwards `containerProps` to the MUI Drawer, so a variant
		// passed here was silently dropped and the drawer has always been `temporary` — a Modal that
		// UNMOUNTS its content on close. Every block below relies on that: they seed their local state
		// from `dsRepricingDrawerData.peek()` on mount, which is only correct because opening another
		// rule remounts them. Making it persistent would freeze the form on the first rule opened.
		// `anchor` is not passed either: the Seeker hardcodes 'right'.
		// The × in the header is the footer's Cancel, nothing more: the backdrop still does not close it.
		onClose={clearDsRepricingSignals}
		footer={
			<DsRepricingDrawerFooter
				onSubmit={onSubmit}
				isLoading={isLoading}
				current={current}
				onStepChange={setCurrent}
				noFit={noFit}
			/>
		}
	>
		<Box ref={topRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			{/* Orientation, not instruction: it explains the split between
			    Setup and a strategy, which you need once on the way in. On
			    every step it was five lines of the same paragraph pushing
			    the wizard down the screen. */}
			{current === 1 && <MovedToSetupBanner/>}
			<Box sx={{ bgcolor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`,
			           borderRadius: '14px', px: 1.5, pb: 1.5 }}>
				<DsRepricingStepper current={current} invalidSteps={invalidSteps} onSelect={setCurrent}/>
			</Box>

			<Box>
				{/* STEP 1 — identity + when the rule applies (period, duration) */}
				<Panel
					step={1}
					current={current}
					title="Basic"
					description="Identify the rule and decide when it applies."
				>
					<Stack gap={2}>
						<SectionCard step={1} title="Identity"
						             subtitle="How you recognise the rule in the list, and how much weight it carries when two rules cover the same price. The lower number wins.">
							{/* Name grows, Priority stays 120px so both inputs share
							    the same 40px height and align cleanly. */}
							<Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
								<Box sx={{ flex: 1 }}><DsRepricingName/></Box>
								<Box sx={{ flex: '0 0 120px' }}><DsRepricingPriority/></Box>
							</Box>
						</SectionCard>

						<SectionCard step={2} title="When does it apply?"
						             subtitle="Which pickup dates the rule covers: a calendar period, or a distance to the pickup. One of the two, narrowed by weekday.">
							<DsRepricingPeriod/>
						</SectionCard>

						<SectionCard step={3} title="Rental lengths"
						             subtitle="The rental lengths this rule prices, in days. A rental no window covers is simply not touched by the rule.">
							<DsRepricingDurations/>
						</SectionCard>
					</Stack>
				</Panel>

				{/* STEP 2 — scope: which of our own products this rule prices, and
				    which slice of the market it is compared against */}
				<Panel
					step={2}
					current={current}
					title="Scope"
					description="Which of our own products this rule prices, and which slice of the market its price is compared against."
				>
					{/* The six filters used to ride loose here, six toggles and
					    six selectors with nothing saying what any of them meant
					    or how they combined. That is how the Sites axis went
					    unnoticed as broken: an empty selector looked like a
					    filter nobody had set. Grouped now by the question each
					    one answers, and each group says the rule out loud.

					    Two blocks since CU-869fe6mf4: what the rule prices (our
					    offices, our faces, our vehicles) and what it is compared
					    against (channels, competitors). The unit sits above both
					    because it governs both halves. The face axes live in
					    the first block on purpose: next to "Which competitors"
					    an "Insurance" field reads as "compare only against this
					    insurance", which is exactly what it does not do. */}
					<Stack gap={2}>
						<SectionCard step={1} title="Compare by"
						             subtitle="The unit the whole market is compared on, and the entity the price ladder is built from.">
							<DsRepricingCompareBy/>
							<Typography component="span"
							            sx={{ display: 'block', mt: 0.875, fontSize: 12, color: dsColorNew.textMuted }}>
								By exact ACRISS code, or grouped by vehicle category.
							</Typography>
						</SectionCard>

						<GroupLabel>What this rule prices</GroupLabel>

						<SectionCard step={2} title="Where"
						             subtitle="The offices this rule reaches. A group covers every office inside it. Empty means every office.">
							<DsRepricingExcludeOffices/>
						</SectionCard>

						<SectionCard step={3} title="Which of our products"
						             subtitle="Which of our own rows this rule prices, by tariff type and insurance. Empty means every row — a rate card that leaves an axis blank is covered on every kind of that axis. This does not decide who the rule is compared against: that is “Which kinds of offer”, below.">
							<DsRepricingFaceScope/>
						</SectionCard>

						<SectionCard step={4}
						             title={compareByCategory ? 'Which categories' : 'Which vehicles'}
						             subtitle={compareByCategory
						                 ? 'The vehicle categories this rule prices. Empty means every category.'
						                 : 'The ACRISS codes this rule prices. Empty means every code.'}>
							{compareByCategory
								? <DsRepricingFilterCategories/>
								: <DsRepricingFilterAcriss/>}
						</SectionCard>

						<GroupLabel>What it is compared against</GroupLabel>

						<SectionCard step={5} title="Which channels"
						             subtitle="The sites and web suppliers whose prices are compared. Both axes apply at once, and an empty one means all of them.">
							<Stack gap={2.5}>
								<DsRepricingExcludeWebs/>
								<DsRepricingExcludeWebSuppliers/>
							</Stack>
						</SectionCard>

						<SectionCard step={6} title="Which competitors"
						             subtitle="Tiers are buckets of providers; the second field names them one by one. Both apply, so a rule can say “the premium tier, but only Hertz”.">
							<Stack gap={2.5}>
								<DsRepricingExcludeProviders/>
								<DsRepricingFilterProviders/>
							</Stack>
						</SectionCard>

						{/* Card 7 (CU-869fh646w) is the one that changes who we
						    compare against, so it sits in this block and never
						    next to card 3. */}
						<SectionCard step={7} title="Which kinds of offer"
						             subtitle="By default a competitor only counts if it sells the same tariff type and the same insurance as our own row — like with like. Switch an axis on to compete against every kind on that axis. This changes who we compare against, never which of our rows this rule prices.">
							<DsRepricingCompareEveryKind/>
						</SectionCard>

						{/* A third group closes the step with the rate cards the
						    scope fits — a consequence of cards 3 and 5, which live
						    in different groups, so it belongs to neither and
						    carries no number (UX D4). */}
						<GroupLabel>Rate cards this scope fits</GroupLabel>

						<DsRepricingRateCardsFit/>
					</Stack>
				</Panel>

				{/* STEP 3 — how the target price is produced */}
				<Panel
					step={3}
					current={current}
					title="Pricing strategy"
					description="Choose how the target price is calculated and adjust its parameters."
				>
					{/* The three blocks are the order the engine applies them:
					    the strategy produces a reference, the undercut moves
					    the bid off it, the guard refuses to let it land
					    somewhere the brand should not be seen. Numbering them
					    says that without a paragraph. */}
					<Stack gap={2}>
						{/* The second sentence is only true of Ranking, and it is
						    the one thing about this rule a revenue manager cannot
						    find out here otherwise: since CU-869fbfnuy the engine
						    prices ABOVE the dearest competitor when the target
						    position is empty, which is the opposite direction to
						    the undercut configured below. Said before the surprise
						    rather than after it (UX gate D3, 2026-10-05).

						    The 5 % is written out here, and this is the one place in
						    the product that does not read it from the event: Activity
						    paints `uplift_pct` off the payload and would follow a
						    change on its own, leaving this sentence lying in silence.
						    It lives in `pms-adapter/src/repricing/strategies/ranking.py`
						    as `INCOMPLETE_MARKET_UPLIFT_PCT`; whoever moves it should
						    land on this comment by grepping for that name (TL, v2.2). */}
						<SectionCard step={1} title="Reference price"
						             subtitle={'What the bid is measured against: the average of the market, '
						                 + 'or the competitor sitting at a chosen position.'
						                 + (isRanking
						                     ? ' If that position is empty because there are fewer competitors '
						                       + 'than it asks for, the engine prices 5 % above the dearest '
						                       + 'competitor in scope instead. This is fixed and cannot be configured.'
						                     : '')}>
							<DsRepricingStrategy/>
						</SectionCard>

						{/* Both modes carry an undercut (criterion change,
						    2026-09-19). Market Trend produces an average and the
						    offset moves the bid off it in either direction;
						    Ranking produces a competitor's price and the offset
						    can only go down. */}
						<SectionCard step={2} title="Undercut"
						             subtitle="How far the bid sits below — or above — that reference. Optional with Market Trend: leave it empty to sit exactly on the average.">
							<DsRepricingUndercut/>
						</SectionCard>

						{/* Also both modes, and for the same reason: each
						    strategy produces a price, and either can land
						    somewhere the operator does not want to be seen. */}
						<SectionCard step={3} title="Competitor guard"
						             subtitle="A floor and a ceiling relative to named competitors, tracking whatever they price today. Applied after the undercut, so it always has the last word.">
							<DsRepricingCompetitorRule/>
						</SectionCard>
					</Stack>
				</Panel>

				{/* STEP 4 — which tethers apply to this strategy */}
				<Panel
					step={4}
					current={current}
					title="Tethering"
					description="Pick which tethering rules from Setup should apply to this strategy. A tether not selected here is invisible to the engine for this rule."
				>
					<SectionCard step={1} title="Tethers this strategy uses"
					             subtitle="Tethering rules are defined once in Setup; each strategy picks which of them apply. One not selected here is invisible to the engine for this rule.">
						<DsRepricingTethers/>
					</SectionCard>
				</Panel>

				{/* STEP 5 — PMS-driven activation gate (optional) */}
				<Panel
					step={5}
					current={current}
					title="My Business"
					description="Optional. Gate this strategy on PMS metrics — fleet availability, pace, forecast… The engine only runs it when every condition below is true at rate-shop time. Empty list = no gate."
				>
					<SectionCard step={1} title="Activation conditions"
					             subtitle="Every condition must be true at rate-shop time for the strategy to run at all. An empty list is no gate, which is the default.">
						<DsRepricingKpiConditions/>
					</SectionCard>
				</Panel>
			</Box>
		</Box>
	</AppFormDrawer>
}